import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { CircularProgress } from "@mui/material";
import { SovosBox, SovosSnackbar, SovosTab, SovosTabs, SovosTypography } from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import AiFixPanel from "../ColumnMappingCanvas/steps/components/AiFixPanel";
import RulesList from "./RulesList";
import SoviSuggestionsBanner from "./SoviSuggestionsBanner";
import {
    StagingAcceptAllFixesModal,
    StagingRejectAllFixesModal,
} from "./StagingBulkFixModals";
import StagingDataGrid from "./StagingDataGrid";
import { applyAcceptedRowPatches, resolveStagingColumnField } from "./stagingGridCellValue";
import { APRS_WEBAPP_SOVI_AI_ENABLED } from "./featureFlags";
import {
    buildPendingSuggestionCellKeys,
    buildSuggestionDetailsByKey,
    buildTranslationRuleName,
    canRequestAiFixes,
    collectPendingSuggestions,
    fixKey,
    getFailedFixKeys,
    getSelectedTranslationFixes,
    partitionFixesForApplication,
    shouldRequestAiFixes,
    waitForTranslationCompletion,
    watchTranslationUntilComplete,
} from "./aiFixApplication";

import {
    STAGING_VIEW_GRID,
    STAGING_VIEW_RULES,
} from "../columnMappingFlowTabs";
import { filterHeadersToMappedColumns } from "../mappedSourceColumns";

const collectApplicableFixes = (aiFixes, acceptedFixes, rejectedFixes, columnNameFilter) => {
    if (!aiFixes?.suggestions) return [];

    const candidates = [];
    for (const col of aiFixes.suggestions) {
        if (columnNameFilter && col.column_name !== columnNameFilter) continue;
        for (const fix of col.fixes || []) {
            const key = fixKey(fix.clb_id, col.column_name);
            if (acceptedFixes.has(key) || rejectedFixes.has(key)) continue;
            if (fix.status !== "applied" || fix.requires_review) continue;
            candidates.push({
                clbId: fix.clb_id,
                columnName: col.column_name,
                originalValue: fix.original_value ?? null,
                newValue: fix.new_value ?? null,
                fixMode: col.fix_mode ?? null,
            });
        }
    }
    return candidates;
};

const StagingDataPanel = forwardRef(function StagingDataPanel({
    profileId,
    projectId,
    mappingId,
    configSet,
    entityName,
    items = [],
    headerItems = [],
    columnMappings = [],
    metadata = {},
    canMutateTranslations = true,
    nonOwnerTooltip,
    aiSuggestionsEnabled = false,
    onAiBusyChange,
    onFetchIssues,
    onFixCell,
    onSuggestFixes,
    onBulkAcceptFixes,
    onCreateStagingTranslation,
    onApplyStagingTranslation,
    onListStagingTranslations,
    onToggleStagingTranslationState,
    onStagingDataChanged,
}, ref) {
    const { i18n } = useGlobalValue();
    const [activeTab, setActiveTab] = useState(STAGING_VIEW_GRID);
    const [issueSummary, setIssueSummary] = useState(null);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);
    const [aiPanelMode, setAiPanelMode] = useState("fixes");
    const [aiFixes, setAiFixes] = useState(null);
    const [loadingAiFixes, setLoadingAiFixes] = useState(false);
    const [acceptedFixes, setAcceptedFixes] = useState(new Set());
    const [acceptedRowPatches, setAcceptedRowPatches] = useState(() => new Map());
    const [rejectedFixes, setRejectedFixes] = useState(new Set());
    const [applyingFixes, setApplyingFixes] = useState(false);
    const [notification, setNotification] = useState(null);
    const [pendingRuleKeys, setPendingRuleKeys] = useState(new Set());
    const [bulkFixModal, setBulkFixModal] = useState(null);

    const handleSubTabChange = useCallback((_, value) => {
        setActiveTab(value);
    }, []);
    const translationRuleIdsRef = useRef(new Map());
    const isMountedRef = useRef(true);
    const contextVersionRef = useRef(0);

    const showAiPanel = APRS_WEBAPP_SOVI_AI_ENABLED && aiSuggestionsEnabled && aiPanelOpen;

    const SYSTEM_COLUMNS = useMemo(() => new Set(["CLB_ImportID", "FieldMetadata"]), []);

    const customColumnHeaders = useMemo(
        () => filterHeadersToMappedColumns(
            headerItems.filter((h) => !SYSTEM_COLUMNS.has(h.headerName)),
            columnMappings,
        ),
        [columnMappings, headerItems, SYSTEM_COLUMNS],
    );

    const refreshIssues = useMemo(
        () => ({ silent = false } = {}) => {
            if (
                !onFetchIssues ||
                !profileId ||
                !projectId ||
                !mappingId ||
                !configSet ||
                !entityName
            )
                return;
            if (!silent) setLoadingIssues(true);
            onFetchIssues(profileId, mappingId, configSet, entityName, projectId)
                .then((result) =>
                    setIssueSummary(result ? { ...result.summary, rows: result.rows || [] } : null),
                )
                .catch(() => setIssueSummary(null))
                .finally(() => {
                    if (!silent) setLoadingIssues(false);
                });
        },
        [onFetchIssues, profileId, projectId, mappingId, configSet, entityName],
    );

    useEffect(() => {
        refreshIssues();
    }, [refreshIssues]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        contextVersionRef.current += 1;
        setAiFixes(null);
        setAcceptedFixes(new Set());
        setAcceptedRowPatches(new Map());
        setRejectedFixes(new Set());
        setAiPanelOpen(false);
        setAiPanelMode("fixes");
        setPendingRuleKeys(new Set());
        translationRuleIdsRef.current.clear();
    }, [profileId, mappingId]);

    const pendingCandidates = useMemo(
        () => collectApplicableFixes(aiFixes, acceptedFixes, rejectedFixes),
        [acceptedFixes, aiFixes, rejectedFixes],
    );
    const patchedItems = useMemo(
        () => applyAcceptedRowPatches(items, acceptedRowPatches),
        [acceptedRowPatches, items],
    );
    const suggestionPartition = useMemo(
        () => partitionFixesForApplication(
            pendingCandidates,
            patchedItems,
            metadata.total ?? patchedItems.length,
        ),
        [metadata.total, patchedItems, pendingCandidates],
    );
    const autoApplicableFixCount = pendingCandidates.length;
    const pendingSuggestions = useMemo(
        () => collectPendingSuggestions(aiFixes, acceptedFixes, rejectedFixes),
        [acceptedFixes, aiFixes, rejectedFixes],
    );
    const suggestedFixCount = pendingSuggestions.length;
    const suggestedRuleCount = suggestionPartition.translationRules.length;
    const suggestionCellKeys = useMemo(
        () => buildPendingSuggestionCellKeys(aiFixes, acceptedFixes, rejectedFixes),
        [acceptedFixes, aiFixes, rejectedFixes],
    );
    const suggestionDetailsByKey = useMemo(
        () => buildSuggestionDetailsByKey(aiFixes, acceptedFixes, rejectedFixes),
        [acceptedFixes, aiFixes, rejectedFixes],
    );

    const handleSuggestFixes = useCallback(
        (columnNames) => {
            if (!onSuggestFixes || !profileId || !projectId) return;
            setLoadingAiFixes(true);
            setAcceptedFixes(new Set());
            setAcceptedRowPatches(new Map());
            setRejectedFixes(new Set());
            onSuggestFixes(profileId, projectId, columnNames)
                .then((result) => setAiFixes(result || null))
                .catch(() => setAiFixes(null))
                .finally(() => setLoadingAiFixes(false));
        },
        [onSuggestFixes, profileId, projectId],
    );

    const runAiFixes = useCallback(() => {
        setAiPanelMode("fixes");
        if (shouldRequestAiFixes({
            trigger: "rerun",
            hasSuggestions: !!aiFixes,
            busy: loadingAiFixes || applyingFixes,
        })) {
            handleSuggestFixes();
        }
    }, [aiFixes, applyingFixes, handleSuggestFixes, loadingAiFixes]);

    const handleOpenAiPanel = useCallback(() => {
        setAiPanelMode("fixes");
        setAiPanelOpen(true);
        if (shouldRequestAiFixes({
            trigger: "open",
            hasSuggestions: !!aiFixes,
            busy: loadingAiFixes || applyingFixes,
        })) {
            handleSuggestFixes();
        }
    }, [aiFixes, applyingFixes, handleSuggestFixes, loadingAiFixes]);

    const handleOpenRulesPanel = useCallback(() => {
        setAiPanelMode("rules");
        setAiPanelOpen(true);
        if (shouldRequestAiFixes({
            trigger: "open",
            hasSuggestions: !!aiFixes,
            busy: loadingAiFixes || applyingFixes,
        })) {
            handleSuggestFixes();
        }
    }, [aiFixes, applyingFixes, handleSuggestFixes, loadingAiFixes]);

    useImperativeHandle(ref, () => ({ runAiFixes }), [runAiFixes]);

    useEffect(() => {
        if (onAiBusyChange) {
            onAiBusyChange(loadingAiFixes || applyingFixes);
        }
        return () => {
            if (onAiBusyChange) {
                onAiBusyChange(false);
            }
        };
    }, [applyingFixes, loadingAiFixes, onAiBusyChange]);

    const applyCandidates = useCallback(
        async (candidates) => {
            if (!candidates.length) {
                return {
                    acceptedKeys: [],
                    failedCount: 0,
                    pendingCount: 0,
                    notStartedCount: 0,
                    changedOnFailure: false,
                };
            }

            const { translationRules, directFixes } = partitionFixesForApplication(
                candidates,
                patchedItems,
                metadata.total ?? patchedItems.length,
            );
            const acceptedKeys = [];
            let failedCount = 0;
            let pendingCount = 0;
            let notStartedCount = 0;
            let changedOnFailure = false;

            if (
                translationRules.length > 0 &&
                onCreateStagingTranslation &&
                onApplyStagingTranslation &&
                onListStagingTranslations
            ) {
                for (let ruleIndex = 0; ruleIndex < translationRules.length; ruleIndex += 1) {
                    const rule = translationRules[ruleIndex];
                    try {
                        const ruleKey = JSON.stringify([
                            rule.columnName,
                            rule.originalValue,
                            rule.newValue ?? "",
                        ]);
                        let created = translationRuleIdsRef.current.get(ruleKey);
                        if (!created) {
                            if (!onListStagingTranslations) {
                                failedCount += rule.fixes.length;
                                continue;
                            }
                            const existingRules = await onListStagingTranslations(
                                profileId,
                                projectId,
                            ).catch(() => null);
                            if (!Array.isArray(existingRules)) {
                                failedCount += rule.fixes.length;
                                continue;
                            }
                            created = existingRules.find(
                                (translation) =>
                                    translation.column_name === rule.columnName &&
                                    translation.operator === "eq" &&
                                    translation.find_value === rule.originalValue &&
                                    translation.replace_value === (rule.newValue ?? ""),
                            );
                            if (created?.id) {
                                translationRuleIdsRef.current.set(ruleKey, created);
                            }
                        }
                        if (!created) {
                            created = await onCreateStagingTranslation({
                                projectId,
                                importProfileId: profileId,
                                name: buildTranslationRuleName(rule),
                                columnName: rule.columnName,
                                operator: "eq",
                                findValue: rule.originalValue,
                                replaceValue: rule.newValue ?? "",
                            }).catch(() => null);
                            if (created?.id) {
                                translationRuleIdsRef.current.set(ruleKey, created);
                            }
                        }
                        if (!created?.id) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        const currentRules = await onListStagingTranslations(
                            profileId,
                            projectId,
                        ).catch(() => null);
                        if (!Array.isArray(currentRules)) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        let currentRule = currentRules.find(
                            (translation) => translation.id === created.id,
                        );
                        if (!currentRule) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        if (currentRule.state === "Inactive") {
                            currentRule = onToggleStagingTranslationState
                                ? await onToggleStagingTranslationState(
                                      currentRule.id,
                                      "Active",
                                  ).catch(() => null)
                                : null;
                            if (!currentRule) {
                                failedCount += rule.fixes.length;
                                continue;
                            }
                        }
                        if (currentRule.state !== "Active") {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        translationRuleIdsRef.current.set(ruleKey, currentRule);

                        const applyResult = await onApplyStagingTranslation(
                            currentRule.id,
                            profileId,
                            projectId,
                        ).catch(() => null);
                        if (!applyResult?.queued) {
                            failedCount += rule.fixes.length;
                            continue;
                        }
                        const completed = onListStagingTranslations
                            ? await waitForTranslationCompletion({
                                  translationId: currentRule.id,
                                  listTranslations: onListStagingTranslations,
                                  profileId,
                                  projectId,
                                  previousRunAt: currentRule.last_run_at,
                              }).catch(() => null)
                            : null;
                        if (!completed) {
                            pendingCount += rule.fixes.length;
                            for (
                                let remainingIndex = ruleIndex + 1;
                                remainingIndex < translationRules.length;
                                remainingIndex += 1
                            ) {
                                notStartedCount +=
                                    translationRules[remainingIndex].fixes.length;
                            }
                            setPendingRuleKeys((prev) => new Set(prev).add(ruleKey));
                            const watcherContext = contextVersionRef.current;
                            const clearPendingRule = () => {
                                setPendingRuleKeys((prev) => {
                                    const next = new Set(prev);
                                    next.delete(ruleKey);
                                    return next;
                                });
                            };
                            (async () => {
                                const lateResult = await watchTranslationUntilComplete({
                                    translationId: currentRule.id,
                                    listTranslations: onListStagingTranslations,
                                    profileId,
                                    projectId,
                                    previousRunAt: currentRule.last_run_at,
                                    isCancelled: () =>
                                        !isMountedRef.current ||
                                        contextVersionRef.current !== watcherContext,
                                });
                                if (
                                    !isMountedRef.current ||
                                    contextVersionRef.current !== watcherContext
                                ) {
                                    return;
                                }
                                clearPendingRule();
                                if (!lateResult) {
                                    setNotification({
                                        type: "error",
                                        message: i18n`"${currentRule.name}" is still running or timed out. Refresh and retry if needed.`,
                                    });
                                    return;
                                }
                                if (lateResult.last_run_outcome === "SUCCESS") {
                                    setAcceptedFixes((prev) => {
                                        const next = new Set(prev);
                                        rule.fixes.forEach((fix) =>
                                            next.add(fixKey(fix.clbId, fix.columnName)),
                                        );
                                        return next;
                                    });
                                }
                                refreshIssues();
                                if (onStagingDataChanged) onStagingDataChanged();
                                setNotification({
                                    type:
                                        lateResult.last_run_outcome === "SUCCESS"
                                            ? "confirmation"
                                            : "error",
                                    message:
                                        lateResult.last_run_outcome === "SUCCESS"
                                            ? i18n`"${currentRule.name}" finished.`
                                            : i18n`"${currentRule.name}" did not complete successfully.`,
                                });
                            })();
                            break;
                        }
                        if (completed.last_run_outcome !== "SUCCESS") {
                            failedCount += rule.fixes.length;
                            changedOnFailure =
                                changedOnFailure || (completed.last_run_changed_rows ?? 0) > 0;
                            continue;
                        }
                        for (const fix of rule.fixes) {
                            acceptedKeys.push(fixKey(fix.clbId, fix.columnName));
                        }
                    } catch (_error) {
                        failedCount += rule.fixes.length;
                    }
                }
            } else if (translationRules.length > 0) {
                failedCount += translationRules.reduce(
                    (total, rule) => total + rule.fixes.length,
                    0,
                );
            }

            if (directFixes.length > 0 && onBulkAcceptFixes) {
                const fixes = directFixes.map((c) => ({
                    clbId: c.clbId,
                    column: c.columnName,
                    newValue: c.newValue ?? null,
                }));
                const result = await onBulkAcceptFixes(profileId, String(projectId), fixes).catch(
                    () => null,
                );
                if (!result || typeof result.accepted !== "number") {
                    failedCount += directFixes.length;
                    return {
                        acceptedKeys,
                        failedCount,
                        pendingCount,
                        notStartedCount,
                        changedOnFailure,
                    };
                }
                const failedFixKeys = getFailedFixKeys(result?.errors || []);
                failedCount += result?.failed ?? 0;
                if (failedFixKeys.size > 0) {
                    directFixes.forEach((fix) => {
                        const key = fixKey(fix.clbId, fix.columnName);
                        if (!failedFixKeys.has(key)) {
                            acceptedKeys.push(key);
                        }
                    });
                } else if (result.accepted === directFixes.length) {
                    directFixes.forEach((fix) => {
                        acceptedKeys.push(fixKey(fix.clbId, fix.columnName));
                    });
                }
            } else if (directFixes.length > 0) {
                failedCount += directFixes.length;
            }

            return {
                acceptedKeys,
                failedCount,
                pendingCount,
                notStartedCount,
                changedOnFailure,
            };
        },
        [
            i18n,
            onApplyStagingTranslation,
            onBulkAcceptFixes,
            onCreateStagingTranslation,
            onListStagingTranslations,
            onToggleStagingTranslationState,
            metadata.total,
            patchedItems,
            profileId,
            projectId,
            refreshIssues,
            onStagingDataChanged,
        ],
    );

    const handleBulkAcceptCandidates = useCallback(async (candidates) => {
        if (!profileId || !projectId) return;
        if (!candidates.length) return;

        setApplyingFixes(true);
        try {
            const {
                acceptedKeys,
                failedCount,
                pendingCount,
                notStartedCount,
                changedOnFailure,
            } = await applyCandidates(candidates);
            if (acceptedKeys.length > 0) {
                setAcceptedFixes((prev) => {
                    const next = new Set(prev);
                    acceptedKeys.forEach((key) => next.add(key));
                    return next;
                });
            }
            if (acceptedKeys.length > 0 || changedOnFailure) {
                refreshIssues();
                if (onStagingDataChanged) onStagingDataChanged();
            }
            if (pendingCount > 0) {
                const notStartedSuffix =
                    notStartedCount > 0
                        ? ` ${i18n`${notStartedCount} additional AI fixes were not started — retry after completion.`}`
                        : "";
                setNotification({
                    type: "info",
                    message: `${i18n`${pendingCount} AI fixes are still queued. Wait for completion before applying more rules.`}${notStartedSuffix}`,
                });
            } else if (failedCount > 0) {
                setNotification({
                    type: "error",
                    message: i18n`${failedCount} AI fixes could not be applied.`,
                });
            }
        } catch (_error) {
            setNotification({
                type: "error",
                message: i18n`AI fixes could not be applied. No additional changes were queued.`,
            });
        } finally {
            setApplyingFixes(false);
        }
    }, [
        i18n,
        applyCandidates,
        profileId,
        projectId,
        refreshIssues,
        onStagingDataChanged,
    ]);

    const handleBulkAcceptSafe = useCallback(
        () => handleBulkAcceptCandidates(pendingCandidates),
        [handleBulkAcceptCandidates, pendingCandidates],
    );

    const handleBulkAcceptRules = useCallback(
        (selectedRuleKeys) => handleBulkAcceptCandidates(
            getSelectedTranslationFixes(
                suggestionPartition.translationRules,
                selectedRuleKeys,
            ),
        ),
        [handleBulkAcceptCandidates, suggestionPartition.translationRules],
    );

    const handleBulkApproveColumn = useCallback(
        async (columnName, fixes) => {
            if (!profileId || !projectId) return;
            const columnFixMode = aiFixes?.suggestions?.find(
                (suggestion) => suggestion.column_name === columnName,
            )?.fix_mode ?? null;
            const candidates = (fixes || [])
                .filter((fix) => {
                    const key = fixKey(fix.clb_id, columnName);
                    return (
                        fix.status === "applied" &&
                        !fix.requires_review &&
                        !acceptedFixes.has(key) &&
                        !rejectedFixes.has(key)
                    );
                })
                .map((fix) => ({
                    clbId: fix.clb_id,
                    columnName,
                    originalValue: fix.original_value ?? null,
                    newValue: fix.new_value ?? null,
                    fixMode: columnFixMode,
                }));
            if (!candidates.length) return;

            setApplyingFixes(true);
            try {
                const {
                    acceptedKeys,
                    failedCount,
                    pendingCount,
                    notStartedCount,
                    changedOnFailure,
                } = await applyCandidates(candidates);
                if (acceptedKeys.length > 0) {
                    setAcceptedFixes((prev) => {
                        const next = new Set(prev);
                        acceptedKeys.forEach((key) => next.add(key));
                        return next;
                    });
                }
                if (acceptedKeys.length > 0 || changedOnFailure) {
                    refreshIssues();
                    if (onStagingDataChanged) onStagingDataChanged();
                }
                if (pendingCount > 0) {
                    const notStartedSuffix =
                        notStartedCount > 0
                            ? ` ${i18n`${notStartedCount} additional AI fixes were not started — retry after completion.`}`
                            : "";
                    setNotification({
                        type: "info",
                        message: `${i18n`${pendingCount} AI fixes are still queued. Wait for completion before applying more rules.`}${notStartedSuffix}`,
                    });
                } else if (failedCount > 0) {
                    setNotification({
                        type: "error",
                        message: i18n`${failedCount} AI fixes could not be applied.`,
                    });
                }
            } catch (_error) {
                setNotification({
                    type: "error",
                    message: i18n`AI fixes could not be applied. No additional changes were queued.`,
                });
            } finally {
                setApplyingFixes(false);
            }
        },
        [
            i18n,
            acceptedFixes,
            aiFixes,
            applyCandidates,
            profileId,
            projectId,
            refreshIssues,
            rejectedFixes,
            onStagingDataChanged,
        ],
    );

    const handleRejectRule = useCallback((columnName, fixes) => {
        const nextRejectedKeys = (fixes || []).map((fix) => fixKey(fix.clb_id, columnName));
        if (!nextRejectedKeys.length) return;
        setRejectedFixes((prev) => {
            const next = new Set(prev);
            nextRejectedKeys.forEach((key) => next.add(key));
            return next;
        });
    }, []);

    const finalizeSuggestionAccept = useCallback(() => {
        refreshIssues({ silent: true });
    }, [refreshIssues]);

    const handleAcceptSuggestion = useCallback(async (detail) => {
        if (!profileId || !projectId || !detail || detail.requiresReview) return false;
        setApplyingFixes(true);
        try {
            const { acceptedKeys, failedCount } = await applyCandidates([{
                clbId: detail.clbId,
                columnName: detail.columnName,
                originalValue: detail.originalValue,
                newValue: detail.newValue,
                fixMode: detail.fixMode,
            }]);
            if (acceptedKeys.length > 0) {
                const columnField = resolveStagingColumnField(customColumnHeaders, detail.columnName);
                setAcceptedFixes((prev) => {
                    const next = new Set(prev);
                    acceptedKeys.forEach((key) => next.add(key));
                    return next;
                });
                setAcceptedRowPatches((prev) => {
                    const next = new Map(prev);
                    const rowId = String(detail.clbId);
                    const existingPatch = next.get(rowId) || {};
                    const rowPatch = {
                        ...existingPatch,
                        [columnField]: detail.newValue,
                    };
                    if (columnField !== detail.columnName) {
                        rowPatch[detail.columnName] = detail.newValue;
                    }
                    next.set(rowId, rowPatch);
                    return next;
                });
                return true;
            }
            if (failedCount > 0) {
                setNotification({
                    type: "error",
                    message: i18n`AI fix could not be applied.`,
                });
            }
            return false;
        } catch (_error) {
            setNotification({
                type: "error",
                message: i18n`AI fix could not be applied.`,
            });
            return false;
        } finally {
            setApplyingFixes(false);
        }
    }, [
        applyCandidates,
        customColumnHeaders,
        i18n,
        profileId,
        projectId,
    ]);

    const handleRejectSuggestion = useCallback((detail) => {
        if (!detail) return;
        handleRejectRule(detail.columnName, [{ clb_id: detail.clbId }]);
    }, [handleRejectRule]);

    const handleCloseBulkFixModal = useCallback(() => {
        if (!applyingFixes) {
            setBulkFixModal(null);
        }
    }, [applyingFixes]);

    const handleRejectAllSuggestedFixes = useCallback(() => {
        setBulkFixModal("reject");
    }, []);

    const handleAcceptAllSuggestedFixes = useCallback(() => {
        setBulkFixModal("accept");
    }, []);

    const handleConfirmRejectAllFixes = useCallback(() => {
        if (!aiFixes?.suggestions) {
            setBulkFixModal(null);
            return;
        }
        setRejectedFixes((prev) => {
            const next = new Set(prev);
            for (const col of aiFixes.suggestions) {
                for (const fix of col.fixes || []) {
                    const key = fixKey(fix.clb_id, col.column_name);
                    if (acceptedFixes.has(key) || next.has(key)) continue;
                    if (fix.status === "applied") {
                        next.add(key);
                    }
                }
            }
            return next;
        });
        setBulkFixModal(null);
    }, [acceptedFixes, aiFixes]);

    const handleConfirmAcceptAllFixes = useCallback(async () => {
        if (!pendingCandidates.length) {
            setBulkFixModal(null);
            return;
        }
        setBulkFixModal(null);
        await handleBulkAcceptCandidates(
            pendingCandidates.map((candidate) => ({
                ...candidate,
                fixMode: "direct",
            })),
        );
    }, [handleBulkAcceptCandidates, pendingCandidates]);

    const handleConfirmAcceptAllFixesAndSaveRules = useCallback(async () => {
        if (!pendingCandidates.length) {
            setBulkFixModal(null);
            return;
        }
        setBulkFixModal(null);
        await handleBulkAcceptCandidates(pendingCandidates);
    }, [handleBulkAcceptCandidates, pendingCandidates]);

    const hasMappingContext = !!(mappingId && configSet && entityName);
    const aiActionsEnabled = canRequestAiFixes({ aiSuggestionsEnabled, onSuggestFixes });
    return (
        <SovosBox
            data-testid="staging-data-panel"
            sx={{
                display: "grid",
                gridTemplateColumns: showAiPanel ? "minmax(0, 1fr) 395px" : "minmax(0, 1fr)",
                gap: 0,
                alignItems: "stretch",
                minHeight: "calc(100vh - 280px)",
            }}
        >
            <SovosBox sx={{ minWidth: 0, p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                <SovosTabs value={activeTab} onChange={handleSubTabChange}>
                    <SovosTab value={STAGING_VIEW_GRID} label={i18n`Staging data`} />
                    <SovosTab value={STAGING_VIEW_RULES} label={i18n`Rules`} />
                </SovosTabs>

                {loadingAiFixes && (
                    <SoviSuggestionsBanner working />
                )}

                {activeTab === STAGING_VIEW_GRID
                    && !loadingAiFixes
                    && (suggestedFixCount > 0 || suggestedRuleCount > 0) && (
                    <SoviSuggestionsBanner
                        fixCount={suggestedFixCount}
                        ruleCount={suggestedRuleCount}
                        onReviewSuggestions={handleOpenAiPanel}
                        onReviewRules={handleOpenRulesPanel}
                    />
                )}

                {activeTab === STAGING_VIEW_GRID &&
                    (!hasMappingContext ? (
                        <SovosBox
                            sx={{
                                p: 4,
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                                {i18n`Save a mapping first to scan the staging data for issues.`}
                            </SovosTypography>
                        </SovosBox>
                    ) : loadingIssues && !issueSummary ? (
                        <SovosBox
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1.5,
                                py: 6,
                            }}
                        >
                            <CircularProgress size={28} />
                            <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                                {i18n`Scanning for data issues…`}
                            </SovosTypography>
                        </SovosBox>
                    ) : (
                        <StagingDataGrid
                            items={patchedItems}
                            columnHeaders={customColumnHeaders}
                            issueSummary={issueSummary}
                            loading={loadingIssues}
                            profileId={profileId}
                            projectId={projectId}
                            canMutate={canMutateTranslations}
                            aiSuggestionsEnabled={aiActionsEnabled}
                            hasAiFixResults={!!aiFixes}
                            onAcceptAllSuggestedFixes={handleAcceptAllSuggestedFixes}
                            onRejectAllSuggestedFixes={handleRejectAllSuggestedFixes}
                            autoApplicableFixCount={autoApplicableFixCount}
                            suggestedFixCount={suggestedFixCount}
                            suggestionCellKeys={suggestionCellKeys}
                            suggestionDetailsByKey={suggestionDetailsByKey}
                            onAcceptSuggestion={handleAcceptSuggestion}
                            onFinalizeSuggestionAccept={finalizeSuggestionAccept}
                            onRejectSuggestion={handleRejectSuggestion}
                            onFixCell={onFixCell}
                            onRefreshIssues={refreshIssues}
                            onOpenAiPanel={handleOpenAiPanel}
                        />
                    ))}

                {activeTab === STAGING_VIEW_RULES && (
                    <RulesList
                        canMutate={canMutateTranslations}
                        nonOwnerTooltip={nonOwnerTooltip}
                        profileId={profileId}
                        projectId={projectId}
                        onList={onListStagingTranslations}
                        onToggleState={onToggleStagingTranslationState}
                    />
                )}
            </SovosBox>

            {showAiPanel && (
                <AiFixPanel
                    acceptedFixes={acceptedFixes}
                    aiFixes={aiFixes}
                    bulkActionLabel={
                        aiPanelMode === "rules"
                            ? i18n`Accept rules (${suggestedRuleCount})`
                            : i18n`Apply all fixes (${autoApplicableFixCount})`
                    }
                    canApply={canMutateTranslations && !applyingFixes && pendingRuleKeys.size === 0}
                    issueSummary={issueSummary}
                    loadingAiFixes={loadingAiFixes || applyingFixes}
                    onBulkAcceptSafe={
                        aiPanelMode === "rules"
                            ? handleBulkAcceptRules
                            : handleBulkAcceptSafe
                    }
                    onBulkApproveColumn={handleBulkApproveColumn}
                    onClose={() => setAiPanelOpen(false)}
                    onRejectRule={handleRejectRule}
                    onRequestFixes={handleSuggestFixes}
                    rejectedFixes={rejectedFixes}
                    selectionEnabled={aiPanelMode === "rules"}
                    title={aiPanelMode === "rules" ? i18n`Rule suggestions` : i18n`Fix Suggestions`}
                    translationRuleGroups={
                        aiPanelMode === "rules"
                            ? suggestionPartition.translationRules
                            : undefined
                    }
                />
            )}
            <StagingRejectAllFixesModal
                busy={applyingFixes}
                onCancel={handleCloseBulkFixModal}
                onConfirm={handleConfirmRejectAllFixes}
                open={bulkFixModal === "reject"}
            />
            <StagingAcceptAllFixesModal
                autoFixCount={autoApplicableFixCount}
                busy={applyingFixes}
                onAcceptAll={handleConfirmAcceptAllFixes}
                onAcceptAllAndSaveRules={handleConfirmAcceptAllFixesAndSaveRules}
                onCancel={handleCloseBulkFixModal}
                open={bulkFixModal === "accept"}
                ruleCount={suggestedRuleCount}
            />
            <SovosSnackbar
                open={!!notification}
                message={notification?.message}
                type={notification?.type}
                onClose={() => setNotification(null)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </SovosBox>
    );
});

export default StagingDataPanel;
